import firebase from 'firebase/app';
import { useCallback, useState } from 'react';

import { SHOW_LOGS } from '../../features/App/App';
import { useAuth } from '../../features/Auth/AuthContext';
import { Offer, OfferStatusState } from '../../models/offers.model';
import { firestore } from '../firebase';
import { FirestoreCollection } from '../firebase.models';

import { useProcessDates } from './useProcessDates.hook';

export function useGetBookedOffersByUid(): [Offer[], () => void, boolean, firebase.FirebaseError | null] {
  const [error, setError] = useState<firebase.FirebaseError | null>(null);
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const { currentUser } = useAuth();

  const getBookedOffersByUid = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Query booked offers where user is the tenant
      const bookedByQuery = await firestore
        .collection(FirestoreCollection.OFFERS_PUBLIC)
        .where('offerStatus.state', '==', OfferStatusState.BOOKED)
        .where('offerStatus.tenant.uid', '==', currentUser.uid)
        .get();

      // Also query user's own offers array for booked offers
      const userDoc = await firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid).get();
      const userOfferIds: string[] = userDoc.data()?.offers || [];

      const chunkSize = 10;
      const chunks = [];
      for (let i = 0; i < userOfferIds.length; i += chunkSize) {
        chunks.push(userOfferIds.slice(i, i + chunkSize));
      }

      const landlordBookedSnapshots = await Promise.all(
        chunks.map((chunk) =>
          firestore
            .collection(FirestoreCollection.OFFERS_PUBLIC)
            .where('id', 'in', chunk)
            .where('offerStatus.state', '==', OfferStatusState.BOOKED)
            .get()
        )
      );

      const combinedOffers: Offer[] = [];

      bookedByQuery.docs.forEach((doc) => {
        const data = doc.data();
        combinedOffers.push({
          id: doc.id,
          ...data,
          ...useProcessDates({
            availability: data.availability,
            availabilityTo: data.availabilityTo,
            offerValidTo: data.offerValidTo,
            createdAt: data.createdAt,
            lastModified: data.lastModified,
          }),
        } as Offer);
      });

      landlordBookedSnapshots.forEach((snapshot) => {
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          combinedOffers.push({
            id: doc.id,
            ...data,
            ...useProcessDates({
              availability: data.availability,
              availabilityTo: data.availabilityTo,
              offerValidTo: data.offerValidTo,
              createdAt: data.createdAt,
              lastModified: data.lastModified,
            }),
          } as Offer);
        });
      });

      const uniqueOffers = Array.from(new Map(combinedOffers.map((offer) => [offer.id, offer])).values());

      SHOW_LOGS && console.log('Unique Booked Offers:', uniqueOffers);
      setOffers(uniqueOffers);
    } catch (error: any) {
      console.error(error);
      setError(error as firebase.FirebaseError);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  return [offers, getBookedOffersByUid, loading, error];
}
